import React from 'react';
import * as PropTypes from "prop-types";
import {withTranslation} from 'react-i18next';
import {Box, Card, CardContent, CardHeader, Grid, IconButton, Tooltip, Typography} from '@mui/material';
import {Link} from "react-router-dom";
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import Constants from "../../../config/constants";
import {getLinkFromListItem} from "../../../utils/helpers";
import useStoredRouteUpdate from "../../../hooks/useStoredRouteUpdate";
import useStoredCliffUpdate from "../../../hooks/useStoredCliffUpdate";
import TooltipComponent from "../../TooltipComponent";
import {useAuth} from "../../../context/auth-context";
import RouteAvatar from "./RouteAvatar";
import {MuiBadge} from "../../MuiBadge";
import {styled} from '@mui/material/styles';
import {format} from "date-fns";
import {toast} from "react-toastify";
import {useAddToCart, useRemoveFromCart} from "../../../hooks/useCart";
import {infitineCardStyle, infitineCardStyleSmall} from "../../../style/useStyles";
import {setSearchQuery, setSearchQueryTerm} from "../../../actions";
import {compose} from "redux";
import {connect} from "react-redux";

const GridItemFooter = styled(Grid)(({ theme }) => ({
    '& .MuiBadge-badge' : {
        fontSize: '0.70rem'
    },
    '& .svg-inline--fa' : {
        fontSize: '1.2em'
    }
}));

const SearchListItemResult = ({
                                  index,
                                  t,
                                  i18n,
                                  item,
                                  screenName,
                                  setSearchQueryTerm,
                                  style = {},
                                  forceSmall = false,
                                  setShowAuthAlert = false,
                                  customerRoutes
                              }) => {
    const auth = useAuth();
    const localStorageItemKey = screenName + '-' + Constants.localStorageKey.searchListItemResult;
    const getAction = (item) => {
        const codeAction = typeof item.listAction !== "undefined" ? item.listAction.action : '';
        let action = Constants.listComponentActions.find(a => a.code === codeAction);
        if (auth.isRouteStored(item, customerRoutes)) {
            action  = Constants.listComponentActions.find(a => a.code === Constants.listAction.saved);
        }

        //auth.isInCart can't be used in hydrateSearchResult : product in cart won't be refreshed when product are added or removed
        if (codeAction === 'add_to_cart' && (auth.isInCart(item.product) || auth.hasParentGuidebookInCart(item))) {
            action  = Constants.listComponentActions.find(a => a.code === Constants.listAction.inCart);
        }
        if (codeAction === 'add_to_cart' && auth.hasParentGuidebookInCart(item)) {
            action  = Constants.listComponentActions.find(a => a.code === Constants.listAction.inCartGuidebook);
        }
        if (typeof action === "undefined") {
            action  = Constants.listComponentActions.find(a => a.code === Constants.listAction.myRoute);
        }

        return action;
    }

    const action = getAction(item);
    const getLanguages = (languageCodes) => {
        const languages = [];
        for (const languageCode of Object.values(languageCodes)) {
            languages.push(t('App.common.language.' + languageCode.toLowerCase()));
        }

        return languages.join(', ');
    }

    const routeUrl = getLinkFromListItem(item, i18n.language);

    const checkParseJsonProperties = [
        'author',
        'guidebookIds',
        'availableLanguages',
        'types',
        'equipmentTypes',
        'listAction',
        'product',
        'productIds',
        'regionIds',
    ];

    checkParseJsonProperties.map((property) => {
        if (typeof item[property] === "string") {
            item[property] = JSON.parse(item[property]);
        }
    })

    const isAvailableInCurrentLanguage = item?.availableLanguages && Object.values(item.availableLanguages).includes(i18n.language);
    let lastUpdateUi = '';
    if (typeof item.lastUpdateUi !== 'undefined' && item.lastUpdateUi) {
        lastUpdateUi =  format(new Date(item.lastUpdateUi * 1000), 'dd/MM/yyyy');
    }

    const {mutateAsync: mutateRouteAsync, status: statusRouteSaving } = useStoredRouteUpdate(i18n.language, item.type);
    const {mutateAsync: mutateCliffAsync, status: statusCliffSaving } = useStoredCliffUpdate(i18n.language);
    const { data: dataRemoveFromCart , mutateAsync: mutateRemoveFromCart, status: statusRemoveFromCart, error: errorRemoveFromCart } = useRemoveFromCart(i18n.language, t('App.screens.CartScreen.removedFromCart'));
    const { data: dataAddToCart , mutateAsync: mutateAddToCart, status: statusAddToCart, error: errorAddToCart }   = useAddToCart(i18n.language, t('App.screens.CartScreen.addedToCart'));
    const addToCart = (product) => {
        try {
            mutateAddToCart(product);
        } catch (e) {
            toast.error(t('common.error.save'));
        }
    }
    const removeFromCart = (product) => {
        try {
            mutateRemoveFromCart(product);
        } catch (e) {
            toast.error(t('common.error.save'));
        }
    }

    const saveRoute = async (action, routeId) => {
        try {
            await mutateRouteAsync({
                id: routeId,
                action: action,
            });
        } catch (e) {
            toast.error(t('common.error.save'));
        }
    }

    const saveCliff = async (action, routeId) => {
        try {
            await mutateCliffAsync({
                id: routeId,
                action: action,
            });
        } catch (e) {
            toast.error(t('common.error.save'));
        }
    }

    const actionFunctions = {
        addToCart: (item) => {
            if (auth.isAuthenticated) {
                addToCart(item.product);
            } else {
                setShowAuthAlert(true);
            }
        },
        removeFromCart: (item) => {
            removeFromCart(item.product);
        },
        save: (item) => {
            if (item.type === Constants.CLIFF) {
                saveCliff('add', item.id);
            } else {
                saveRoute('add', item.id);
            }
        },
        unsave: (item) => {
            if (item.type === Constants.CLIFF) {
                saveCliff('remove', item.id);
            } else {
                saveRoute('remove', item.id);
            }
        }
    };

    const beforeRedirect = () => {
        localStorage.setItem(localStorageItemKey, index);

        setSearchQueryTerm(item?._formatted?.highlightRoutes);
    }

    const titleContent = () => {
        return (
            <Typography variant="cardTitle">
                <Link to={routeUrl} className={!isAvailableInCurrentLanguage  ? "disabled" : ""} onClick={beforeRedirect}>
                    {item.label}
                </Link>
                {!isAvailableInCurrentLanguage && <span style={{marginLeft: '10px'}}>
                                <TooltipComponent title={t("SecteurListComponent.popover.language_availables", {languages: getLanguages(item.availableLanguages)})} color="primary">
                                    <FontAwesomeIcon icon={['fa', 'globe']} size="sm" />
                                </TooltipComponent>
                            </span>}
                {lastUpdateUi && <span style={{marginLeft: '10px'}}>
                                <TooltipComponent title={t("SecteurListComponent.popover.last_update", {lastUpdateUi: lastUpdateUi})} color="primary">
                                    <FontAwesomeIcon icon={['fa', 'fire']} size="sm" />
                                </TooltipComponent>
                            </span>}
            </Typography>);
    }

    const title = () => {
        return (
            <Box display="flex" alignItems="center">
                <Box flexGrow={1}>
                    {titleContent()}
                </Box>
                <Box sx={{textAlign: 'right'}}>
                    <span style={{fontWeight: 700}} className={item.avatarColor}>{item.region.label}</span> <br/>
                    {screenName === Constants.SCREEN_NAME.MY_ROUTE && item.type === Constants.MULTIPITCH && item.faceLabelList && <span style={{fontSize: '0.75rem', fontWeight: 700, marginLeft: '5px',}}>{item.faceLabelList}</span>}
                    {item?.product?.priceLabel && action?.code === "add_to_cart" &&
                    <span style={{fontSize: '0.9rem', fontWeight: 700, marginLeft: '5px',}}>
                        {item.product.priceLabel}
                                        </span>
                    }
                </Box>
            </Box>
        );
    }

    const cardStyle = !forceSmall ? {...style,  ...infitineCardStyle} : {...style, ...infitineCardStyleSmall};
    return (
        <Card
            className={forceSmall ? 'cardForceSmall' : 'card'}
            key={item.slug}
            variant={"outlined"}
            style={cardStyle}
        >
            <CardHeader
                sx={{padding: '8px'}}
                avatar={<RouteAvatar
                    item={item}
                    url={routeUrl}
                    beforeRedirect={beforeRedirect}
                />}
                title={<>{title()} </>}
            />
            <CardContent style={{padding: "0px", paddingBottom: "8px", height: '49px'}}>
                <Grid container spacing={0} justifyContent={"space-between"} alignItems="center" sx={{textAlign: 'center'}}>
                    {/*{!forceSmall &&*/}
                    {/*<Box sx={{ display: { sm: "none", md: "block" } }}>*/}
                    {/*    <GridItemFooter item md={4}>*/}
                    {/*        <Box display="flex" alignItems="center">*/}
                    {/*            <Box pr={2}>*/}
                    {/*                <RouteAvatar*/}
                    {/*                    item={item}*/}
                    {/*                    url={routeUrl}*/}
                    {/*                />*/}
                    {/*            </Box>*/}
                    {/*            <Box>*/}
                    {/*                {titleContent()}*/}
                    {/*            </Box>*/}
                    {/*        </Box>*/}
                    {/*    </GridItemFooter>*/}
                    {/*</Box>*/}
                    {/*}*/}


                    <GridItemFooter item xs md={forceSmall ? true : 1}>
                        {item.enabledRouteCount > 0 &&
                        <>
                            <Typography variant="body1" gutterBottom><b>{item.enabledRouteCount}</b> {t('searchResults.routes')}</Typography>
                        </>
                        }
                        {item.type === Constants.MULTIPITCH && <Typography variant="body1" gutterBottom>
                            <TooltipComponent title={t("searchResults.difficulty.help")}>
                                {item.difficultyEquipment}
                            </TooltipComponent>
                        </Typography>}
                    </GridItemFooter>
                    <GridItemFooter item xs md={forceSmall ? true : 1}>
                        {item.grades && item.grades !== "/" &&
                        <TooltipComponent title={t("searchResults.grades." + item.type)} color="primary">
                            <MuiBadge badgeContent={item.grades} color="tertiary">
                                <span className="primary"><FontAwesomeIcon icon={["fas", "tachometer-alt"]}  style={{fontSize: "1.5em"}} /></span>
                            </MuiBadge>
                        </TooltipComponent>
                        }
                    </GridItemFooter>
                    <GridItemFooter item xs md={forceSmall ? true : 2}>
                        {item.length > 0 &&
                        <>
                            <TooltipComponent title={t("searchResults.lengthMaxHelp." + item.type)} color="primary">
                                {item.type === Constants.CLIFF && <span className={'maxIconText'}>{t('searchResults.lengthMax')}</span>}
                                <FontAwesomeIcon icon={["fas", "arrows-alt-v"]} size="lg" className="primary" style={{position: 'relative', paddingRight: '0px', marginLeft: '0.8rem', fontSize: '1.5em'}}/>
                                <Typography variant="body1" style={{display: 'inline-block', }}> {item.length}{t('searchResults.length')}</Typography>
                            </TooltipComponent>
                        </>
                        }
                    </GridItemFooter>

                    <GridItemFooter item xs md={forceSmall ? true : 1}>
                        {action && action.function === 'signUp' &&
                            <IconButton onClick={() => actionFunctions[action.function](item)} color={action.color}>
                                <FontAwesomeIcon icon={action.icon} />
                            </IconButton>
                        }
                        {action && action.function === 'addToCart' && item?.product?.price !== null &&
                            <IconButton onClick={() => actionFunctions[action.function](item)} color={action.color}>
                                <FontAwesomeIcon icon={action.icon} />
                            </IconButton>
                        }
                        {action && action.function === 'removeFromCart' &&
                            <IconButton onClick={() => actionFunctions[action.function](item)} color={action.color}>
                                <FontAwesomeIcon icon={action.icon} />
                            </IconButton>
                        }
                        {action && action.function === 'disabledRemoveFromCart' && <Tooltip title={t("SecteurListComponent.popover.remove_in_cart_guidebook")}>
                            <IconButton color={action.color}>
                                <FontAwesomeIcon icon={action.icon}  />
                            </IconButton>
                        </Tooltip>}
                        {action && action.function !== 'addToCart' && action.function !== 'removeFromCart' && action.function !== 'disabledRemoveFromCart' &&
                            <IconButton onClick={() => actionFunctions[action.function] !== undefined && actionFunctions[action.function](item)}
                                        disabled={statusRouteSaving === "pending" || statusCliffSaving === "pending"}
                            color={(statusRouteSaving === "pending" || statusCliffSaving === "pending") ? "secondary" : action.color}
                            >
                                {(statusRouteSaving === "pending" || statusCliffSaving === "pending") && <FontAwesomeIcon icon={["fas", "spinner"]} spin />}
                                {statusRouteSaving !== "pending" && statusCliffSaving !== "pending" && <FontAwesomeIcon icon={action.icon} />}
                            </IconButton>
                        }
                    </GridItemFooter>
                    {/*{screenName !== Constants.SCREEN_NAME.MY_ROUTE && <Grid item xs md={forceSmall ? true : 2} className={'gridItemFooter'}>*/}
                    {/*    {item?.product?.priceLabel && action?.code === "add_to_cart" && <span style={{fontSize: '0.9rem', fontWeight: 700, marginLeft: '5px',}}>*/}
                    {/*        {item.product.priceLabel}*/}
                    {/*    </span>}*/}
                    {/*</Grid>}*/}
                    {/*{!forceSmall && <Box sx={{ display: { sm: "none", md: "block" } }}>*/}
                    {/*    <GridItemFooter item xs md={3}>*/}
                    {/*        <Box style={{textAlign: 'right'}}>*/}
                    {/*            <span style={{fontWeight: 700}} className={item.avatarColor}>{item.region.label}</span> <br/>*/}
                    {/*            {item?.product?.price !== null && action?.code === "add_to_cart" &&*/}
                    {/*            <span style={{fontSize: '0.9rem', fontWeight: 700, marginLeft: '5px',}}>*/}
                    {/*                {item.product.priceLabel}*/}
                    {/*            </span>*/}
                    {/*            }*/}
                    {/*        </Box>*/}
                    {/*    </GridItemFooter>*/}
                    {/*</Box>}*/}
                </Grid>
            </CardContent>
        </Card>
    );
};

SearchListItemResult.defaultProps = {
    hits: PropTypes.array,
};

const mapDispatchToProps = dispatch => {
    return {
        setSearchQueryTerm: (term) => {
            if (term === '') {
                term = null;
            }
            dispatch(setSearchQueryTerm(term))
        }
    }
};

export default compose(
    withTranslation(),
    connect(
        null,
        mapDispatchToProps
    )
)(SearchListItemResult);
